import { Dropdown, DropdownOption, Input, SegmentedControl } from '../ui';
import { TIMEFRAME_OPTIONS, TimeframeKey, CustomRange } from './timeframe';

interface SalesActionBarProps {
  viewMode: 'kanban' | 'list';
  onViewModeChange: (mode: 'kanban' | 'list') => void;
  timeframe: TimeframeKey;
  onTimeframeChange: (value: TimeframeKey) => void;
  customRange: CustomRange;
  onCustomRangeChange: (value: CustomRange) => void;
  sourceFilter: string;
  sourceOptions: DropdownOption[];
  onSourceFilterChange: (value: string) => void;
}

/**
 * Filter row above the pipeline — timeframe (+ from/to when custom) and source on the
 * left, Board | List on the right. Filters apply to both views and to the KPI strip.
 */
export default function SalesActionBar({
  viewMode,
  onViewModeChange,
  timeframe,
  onTimeframeChange,
  customRange,
  onCustomRangeChange,
  sourceFilter,
  sourceOptions,
  onSourceFilterChange,
}: SalesActionBarProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        flexWrap: 'wrap',
        marginBottom: 'var(--space-4)',
      }}
    >
      {/* Phones: the two filters share one row at full width; desktop keeps fixed widths */}
      <div className="w-full md:w-auto" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <div className="flex-1 min-w-[130px] md:flex-none md:w-[160px]">
          <Dropdown
            size="sm"
            options={TIMEFRAME_OPTIONS}
            value={timeframe}
            onChange={(e) => onTimeframeChange(e.target.value as TimeframeKey)}
          />
        </div>
        {timeframe === 'custom' ? (
          <>
            <Input
              type="date"
              fieldSize="sm"
              aria-label="From"
              value={customRange.from}
              max={customRange.to || undefined}
              onChange={(e) => onCustomRangeChange({ ...customRange, from: e.target.value })}
              style={{ width: '150px' }}
            />
            <span style={{ color: 'var(--text-faint)', fontSize: 'var(--text-sm)' }}>to</span>
            <Input
              type="date"
              fieldSize="sm"
              aria-label="To"
              value={customRange.to}
              min={customRange.from || undefined}
              onChange={(e) => onCustomRangeChange({ ...customRange, to: e.target.value })}
              style={{ width: '150px' }}
            />
          </>
        ) : null}
        <div className="flex-1 min-w-[130px] md:flex-none md:w-[200px]">
          <Dropdown
            size="sm"
            options={sourceOptions}
            value={sourceFilter}
            onChange={(e) => onSourceFilterChange(e.target.value)}
            placeholder="All sources"
          />
        </div>
      </div>
      <SegmentedControl
        options={[
          { id: 'kanban', label: 'Board' },
          { id: 'list', label: 'List' },
        ]}
        value={viewMode}
        onChange={(id) => onViewModeChange(id as 'kanban' | 'list')}
      />
    </div>
  );
}
